import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plane, Briefcase, Gem, Sparkles, Wine, Star, Flower, Baby, User, ArrowRight, Calendar, Clock, MapPin, Users } from "lucide-react";

const pkgIcons = { airport: Plane, corporate: Briefcase, vip: Gem, events: Sparkles };
const addonIcons = { wine: Wine, sparkles: Sparkles, star: Star, flower: Flower, baby: Baby, user: User };

export default function Booking() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState(packageId || "");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(""); // the code currently sent to backend
  const [form, setForm] = useState({
    pickup_date: "", pickup_time: "", pickup_address: "", dropoff_address: "",
    passengers: 1, customer_name: "", customer_email: "", customer_phone: "", notes: "", hours: 2
  });

  useEffect(() => {
    Promise.all([api.get("/packages"), api.get("/addons")])
      .then(([p, a]) => { setPackages(p.data); setAddons(a.data); })
      .catch(() => toast.error("Failed to load options"));
  }, []);

  const currentPkg = packages.find(p => p.id === selectedPkg);
  const isHourly = currentPkg?.pricing_model === "hourly";
  const minHours = currentPkg?.min_hours || 1;
  const addonsPreviewTotal = selectedAddons.reduce((s, id) => s + (addons.find(a => a.id === id)?.price || 0), 0);

  // Live quote — re-runs when relevant inputs change
  useEffect(() => {
    if (!selectedPkg) { setQuote(null); return; }
    setQuoting(true);
    const t = setTimeout(async () => {
      try {
        const payload = {
          package_id: selectedPkg,
          addons: selectedAddons,
          coupon_code: appliedCoupon || undefined,
        };
        if (isHourly) {
          payload.hours = Number(form.hours) || 0;
        } else {
          const pickup = form.pickup_address.trim();
          const dropoff = form.dropoff_address.trim();
          if (pickup.length < 5 || dropoff.length < 5) { setQuote(null); setQuoting(false); return; }
          payload.pickup_address = pickup;
          payload.dropoff_address = dropoff;
        }
        const res = await api.post("/quote", payload);
        setQuote(res.data);
      } catch {
        setQuote(null);
      } finally {
        setQuoting(false);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [selectedPkg, isHourly, form.pickup_address, form.dropoff_address, form.hours, selectedAddons, appliedCoupon]);

  const toggleAddon = id => setSelectedAddons(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedPkg) { toast.error("Select a service package"); return; }
    if (!quote || !quote.miles) { toast.error("We need valid pickup & drop-off addresses to calculate your fare"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        package_id: selectedPkg,
        addons: selectedAddons,
        coupon_code: appliedCoupon || undefined,
        ...form,
        passengers: Number(form.passengers),
      });
      toast.success("Booking created — proceed to payment");
      navigate(`/booking/${res.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create booking");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-page">
      <Navbar/>
      <div className="pt-32 pb-20 px-6 md:px-12 max-w-7xl mx-auto">
        <p className="eyebrow mb-3">Reservation</p>
        <h1 className="font-serif text-4xl md:text-6xl mb-2">Book your <span className="italic gradient-gold">experience</span></h1>
        <div className="divider"/>
        <p className="text-[#6b7c8a] max-w-2xl mb-12">Select a service, add the finishing touches, and we'll take care of the rest.</p>

        <form onSubmit={submit} className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-10">
            {/* Service */}
            <section>
              <h2 className="font-serif text-2xl mb-5">1. Select Service</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {packages.map(p => {
                  const Icon = pkgIcons[p.id] || Gem;
                  const active = selectedPkg === p.id;
                  return (
                    <button type="button" key={p.id} data-testid={`pkg-${p.id}`}
                      onClick={() => setSelectedPkg(p.id)}
                      className={`card-dark p-5 text-left ${active ? "border-[#5DCFE5]" : ""}`}>
                      <div className="flex items-center justify-between mb-2">
                        <Icon size={20} className="text-[#5DCFE5]"/>
                        <span className="text-xs text-[#5DCFE5]">{p.pricing_model === "hourly" ? `$${p.hourly_rate}/hr` : "$20 + $3/mi"}</span>
                      </div>
                      <div className="font-serif text-lg">{p.name}</div>
                      <div className="text-xs text-[#6b7c8a] mt-1">{p.tagline}</div>
                      {p.pricing_model === "hourly" && <div className="text-[10px] text-[#4a5868] mt-1 uppercase tracking-widest">{p.min_hours} hr min</div>}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Add-ons */}
            <section>
              <h2 className="font-serif text-2xl mb-5">2. Enhance Your Ride</h2>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {addons.map(a => {
                  const Icon = addonIcons[a.icon] || Star;
                  const active = selectedAddons.includes(a.id);
                  return (
                    <button type="button" key={a.id} data-testid={`addon-${a.id}`}
                      onClick={() => toggleAddon(a.id)}
                      className={`p-4 rounded-xl border text-left transition ${active ? "border-[#5DCFE5] bg-[#0c1825]" : "border-[#1a2838] hover:border-[#4a5868]"}`}>
                      <Icon size={18} className={active ? "text-[#5DCFE5] mb-2" : "text-[#6b7c8a] mb-2"}/>
                      <div className="text-sm font-medium">{a.name}</div>
                      <div className="text-xs text-[#5DCFE5] mt-1">+${a.price.toFixed(0)}</div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Trip Details */}
            <section>
              <h2 className="font-serif text-2xl mb-5">3. Trip Details</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="eyebrow flex items-center gap-2 mb-2"><Calendar size={12}/> Pickup Date</label>
                  <input data-testid="input-date" required type="date" value={form.pickup_date} onChange={e => setForm({...form, pickup_date: e.target.value})} className="input-dark" min={new Date().toISOString().split("T")[0]}/>
                </div>
                <div>
                  <label className="eyebrow flex items-center gap-2 mb-2"><Clock size={12}/> Pickup Time</label>
                  <input data-testid="input-time" required type="time" value={form.pickup_time} onChange={e => setForm({...form, pickup_time: e.target.value})} className="input-dark"/>
                </div>
                <div className="md:col-span-2">
                  <label className="eyebrow flex items-center gap-2 mb-2"><MapPin size={12}/> Pickup Address</label>
                  <AddressAutocomplete
                    testId="input-pickup"
                    required
                    value={form.pickup_address}
                    onChange={(v) => setForm({...form, pickup_address: v})}
                    placeholder="Start typing… e.g. 123 Ocean Drive, Miami Beach"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="eyebrow flex items-center gap-2 mb-2"><MapPin size={12}/> {isHourly ? "Drop-off Address (optional)" : "Drop-off Address"}</label>
                  <AddressAutocomplete
                    testId="input-dropoff"
                    required={!isHourly}
                    value={form.dropoff_address}
                    onChange={(v) => setForm({...form, dropoff_address: v})}
                    placeholder={isHourly ? "Leave blank if returning to pickup" : "Start typing… e.g. Miami International Airport"}
                  />
                </div>
                {isHourly && (
                  <div className="md:col-span-2">
                    <label className="eyebrow flex items-center gap-2 mb-2"><Clock size={12}/> Total Hours Booked · ${currentPkg.hourly_rate}/hr · {currentPkg.min_hours} hr min</label>
                    <div className="flex items-center gap-3">
                      <button type="button" data-testid="hours-minus"
                        onClick={() => setForm({...form, hours: Math.max(minHours, Number(form.hours) - 1)})}
                        className="w-10 h-10 rounded-full border border-[#1a2838] hover:border-[#5DCFE5] text-[#5DCFE5] text-xl">−</button>
                      <input data-testid="input-hours" required type="number" min={minHours} max="24" step="0.5"
                        value={form.hours} onChange={e => setForm({...form, hours: e.target.value})}
                        className="input-dark text-center text-xl font-serif flex-1"/>
                      <button type="button" data-testid="hours-plus"
                        onClick={() => setForm({...form, hours: Math.min(24, Number(form.hours) + 1)})}
                        className="w-10 h-10 rounded-full border border-[#1a2838] hover:border-[#5DCFE5] text-[#5DCFE5] text-xl">+</button>
                      <div className="text-[#5DCFE5] font-serif text-xl min-w-[80px] text-right">${(Number(form.hours) * currentPkg.hourly_rate).toFixed(0)}</div>
                    </div>
                  </div>
                )}
                <div>
                  <label className="eyebrow flex items-center gap-2 mb-2"><Users size={12}/> Passengers</label>
                  <input data-testid="input-passengers" required type="number" min="1" max="14" value={form.passengers} onChange={e => setForm({...form, passengers: e.target.value})} className="input-dark"/>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section>
              <h2 className="font-serif text-2xl mb-5">4. Your Information</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <input data-testid="input-name" required placeholder="Full Name" value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} className="input-dark"/>
                <input data-testid="input-email" required type="email" placeholder="Email" value={form.customer_email} onChange={e => setForm({...form, customer_email: e.target.value})} className="input-dark"/>
                <input data-testid="input-phone" required placeholder="Phone Number" value={form.customer_phone} onChange={e => setForm({...form, customer_phone: e.target.value})} className="input-dark md:col-span-2"/>
                <textarea data-testid="input-notes" placeholder="Special requests, flight number, etc." value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="input-dark md:col-span-2" rows={3}/>
              </div>
            </section>
          </div>

          {/* Summary */}
          <aside className="lg:col-span-1">
            <div className="card-dark p-6 sticky top-28">
              <p className="eyebrow mb-3">Quote Summary</p>
              <div className="divider"/>

              {currentPkg ? (
                <div className="text-sm mb-3 pb-3 border-b border-[#1a2838]">
                  <div className="text-[#f0f6fb]">{currentPkg.name}</div>
                  <div className="text-xs text-[#6b7c8a] mt-1">{currentPkg.duration}</div>
                </div>
              ) : (
                <p className="text-sm text-[#6b7c8a] mb-4">Select a service.</p>
              )}

              {/* Fare breakdown — hourly OR distance */}
              <div className="text-sm space-y-2">
                {isHourly ? (
                  <>
                    <div className="flex justify-between py-1">
                      <span className="text-[#b8c5d1]">Hours · {form.hours || 0} × ${currentPkg?.hourly_rate || 100}</span>
                      <span className="text-[#5DCFE5]">${((Number(form.hours) || 0) * (currentPkg?.hourly_rate || 100)).toFixed(2)}</span>
                    </div>
                    {Number(form.hours) < minHours && (
                      <div className="text-xs text-amber-300 py-1">Minimum {minHours} hours required</div>
                    )}
                    <div className="flex justify-between py-1 font-medium border-t border-[#1a2838] pt-2">
                      <span>Trip cost</span>
                      <span className="text-[#5DCFE5]" data-testid="trip-cost">${(quote?.trip_cost || 0).toFixed(2)}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between py-1">
                      <span className="text-[#b8c5d1]">Base fare</span>
                      <span className="text-[#5DCFE5]">$20.00</span>
                    </div>
                    {quoting ? (
                      <div className="flex justify-between py-1 text-[#6b7c8a]">
                        <span>Calculating distance…</span>
                        <span className="shimmer w-16 h-3 rounded"/>
                      </div>
                    ) : quote && quote.miles ? (
                      <>
                        <div className="flex justify-between py-1 text-[#b8c5d1]">
                          <span>Distance · {quote.miles} mi × $3</span>
                          <span>${quote.distance_cost.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between py-1 font-medium border-t border-[#1a2838] pt-2">
                          <span>Trip cost</span>
                          <span className="text-[#5DCFE5]" data-testid="trip-cost">${quote.trip_cost.toFixed(2)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-[#4a5868] py-2 italic">
                        Enter pickup & drop-off addresses to see your fare.
                      </div>
                    )}
                  </>
                )}

                {selectedAddons.length > 0 && (
                  <div className="pt-2 border-t border-[#1a2838] space-y-1">
                    {selectedAddons.map(id => {
                      const a = addons.find(x => x.id === id);
                      return a && (
                        <div key={id} className="flex justify-between py-1 text-[#b8c5d1]">
                          <span>+ {a.name}</span>
                          <span>${a.price.toFixed(2)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Coupon code */}
              <div className="mt-4 pt-4 border-t border-[#1a2838]">
                {!appliedCoupon ? (
                  <>
                    <label className="eyebrow mb-2 block">Promo / Coupon Code</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        data-testid="input-coupon"
                        value={coupon}
                        onChange={e => setCoupon(e.target.value.toUpperCase())}
                        placeholder="ENTER CODE"
                        className="input-dark flex-1 uppercase tracking-widest text-sm"
                      />
                      <button
                        type="button"
                        data-testid="apply-coupon-btn"
                        onClick={() => { if (coupon.trim()) setAppliedCoupon(coupon.trim()); }}
                        disabled={!coupon.trim()}
                        className="px-4 py-2 rounded-lg border border-[#5DCFE5] text-[#5DCFE5] text-xs tracking-widest uppercase hover:bg-[#5DCFE5] hover:text-[#050a12] transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Apply
                      </button>
                    </div>
                  </>
                ) : (
                  <div>
                    <label className="eyebrow mb-2 block">Coupon</label>
                    {quote?.coupon ? (
                      <div className="flex items-center justify-between bg-[#0a3a3a]/40 border border-[#5DCFE5]/40 rounded-lg px-3 py-2">
                        <div>
                          <div className="text-[#5DCFE5] font-medium text-sm tracking-wider">{quote.coupon.code}</div>
                          <div className="text-[10px] text-[#6b7c8a] uppercase tracking-widest">
                            {quote.coupon.type === "percent" ? `${quote.coupon.value}% off` : `$${quote.coupon.value} off`}
                          </div>
                        </div>
                        <button type="button" onClick={() => { setAppliedCoupon(""); setCoupon(""); }}
                          data-testid="remove-coupon-btn"
                          className="text-xs text-[#6b7c8a] hover:text-red-300 uppercase tracking-widest">Remove</button>
                      </div>
                    ) : quote?.coupon_error ? (
                      <div className="flex items-center justify-between bg-red-950/30 border border-red-700/40 rounded-lg px-3 py-2">
                        <div className="text-red-300 text-xs">{quote.coupon_error}</div>
                        <button type="button" onClick={() => { setAppliedCoupon(""); setCoupon(""); }}
                          className="text-xs text-red-300 uppercase tracking-widest">Remove</button>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              {quote?.discount > 0 && (
                <div className="flex justify-between mt-3 text-sm">
                  <span className="text-[#5DCFE5]">Discount</span>
                  <span className="text-[#5DCFE5]" data-testid="discount-line">−${quote.discount.toFixed(2)}</span>
                </div>
              )}

              <div className="border-t border-[#1a2838] mt-4 pt-4 flex justify-between items-center">
                <span className="eyebrow">Total</span>
                <span className="font-serif text-3xl text-[#5DCFE5]" data-testid="quote-total">
                  ${quote && (quote.miles || quote.hours) ? quote.total.toFixed(2) : (addonsPreviewTotal > 0 ? `+${addonsPreviewTotal.toFixed(2)}` : "—")}
                </span>
              </div>

              <button type="submit" data-testid="continue-checkout-btn" disabled={submitting || !selectedPkg || (isHourly ? Number(form.hours) < minHours : !quote?.miles)} className="btn-gold w-full justify-center mt-6">
                {submitting ? "Creating..." : <>Continue to Payment <ArrowRight size={16}/></>}
              </button>
              <p className="text-[11px] text-[#4a5868] mt-3 text-center">Secure checkout via Stripe / PayPal</p>
            </div>
          </aside>
        </form>
      </div>
      <Footer/>
    </div>
  );
}
